import React, { useState, useEffect, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Zap, Target, Clock, RotateCcw, Trophy } from "lucide-react";
import { toast } from "sonner";

export default function Typing() {
  const { data, isLoading } = useQuery({
    queryKey: ["passages"],
    queryFn: async () => (await api.get("/typing/passages")).data,
  });
  const passages = data?.passages || [];
  const [selected, setSelected] = useState(null);

  if (isLoading)
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (selected) return <TypingTest passage={selected} onExit={() => setSelected(null)} />;

  return (
    <div className="space-y-6" data-testid="typing-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">Speed & Accuracy</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">Typing Test</h1>
        <p className="text-muted-foreground mt-1">Live WPM and accuracy tracking. Type the passage as fast and accurately as you can.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {passages.map((p) => (
          <div key={p.id} className="p-6 rounded-md border border-border bg-card flex flex-col hover:-translate-y-1 transition-transform" data-testid={`passage-card-${p.id}`}>
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">{p.difficulty} · {p.text.split(" ").length} words</span>
            <h3 className="font-heading font-bold text-lg mb-2">{p.title}</h3>
            <p className="text-sm text-muted-foreground mb-6 flex-1 line-clamp-3">{p.text}</p>
            <Button className="rounded-md gap-2" onClick={() => setSelected(p)} data-testid={`start-typing-${p.id}`}>
              <Zap className="w-4 h-4" /> Start
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function TypingTest({ passage, onExit }) {
  const target = passage.text;
  const [input, setInput] = useState("");
  const [started, setStarted] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [done, setDone] = useState(false);
  const [finalStats, setFinalStats] = useState(null);
  const inputRef = useRef();
  const timerRef = useRef();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (started && !done) {
      timerRef.current = setInterval(() => {
        setElapsed((Date.now() - startTime) / 1000);
      }, 100);
      return () => clearInterval(timerRef.current);
    }
  }, [started, done, startTime]);

  const correctChars = input.split("").filter((c, i) => c === target[i]).length;
  const errors = input.split("").filter((c, i) => c !== target[i]).length;
  const accuracy = input.length ? Math.round((correctChars / input.length) * 100) : 100;
  const wpm = elapsed > 0 ? Math.round((correctChars / 5) / (elapsed / 60)) : 0;

  const finish = useCallback(async (finalInput, finalElapsed) => {
    clearInterval(timerRef.current);
    setDone(true);
    const cc = finalInput.split("").filter((c, i) => c === target[i]).length;
    const err = finalInput.split("").filter((c, i) => c !== target[i]).length;
    const acc = finalInput.length ? Math.round((cc / finalInput.length) * 100) : 100;
    const finalWpm = finalElapsed > 0 ? Math.round((cc / 5) / (finalElapsed / 60)) : 0;
    const stats = { wpm: finalWpm, accuracy: acc, duration: Math.round(finalElapsed), errors: err };
    setFinalStats(stats);
    try {
      await api.post("/typing/submit", { passage_id: passage.id, typed_chars: finalInput.length, ...stats });
    } catch {
      toast.error("Could not save result.");
    }
  }, [passage.id, target]);

  const handleChange = (e) => {
    if (done) return;
    const val = e.target.value;
    if (!started) {
      setStarted(true);
      setStartTime(Date.now());
    }
    if (val.length <= target.length) {
      setInput(val);
      if (val.length === target.length) {
        finish(val, (Date.now() - startTime) / 1000 || elapsed);
      }
    }
  };

  const reset = () => {
    clearInterval(timerRef.current);
    setInput(""); setStarted(false); setStartTime(null); setElapsed(0); setDone(false); setFinalStats(null);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  if (done && finalStats) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12" data-testid="typing-result">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Trophy className="w-8 h-8 text-primary" />
        </div>
        <h1 className="font-heading text-4xl font-black tracking-tighter mb-8">Test Complete</h1>
        <div className="grid grid-cols-3 gap-4 mb-10">
          <ResultStat icon={Zap} label="WPM" value={finalStats.wpm} testid="result-wpm" />
          <ResultStat icon={Target} label="Accuracy" value={`${finalStats.accuracy}%`} testid="result-accuracy" />
          <ResultStat icon={Clock} label="Time" value={`${finalStats.duration}s`} testid="result-time" />
        </div>
        <div className="flex gap-3 justify-center">
          <Button variant="outline" className="rounded-md gap-2" onClick={reset} data-testid="typing-retry"><RotateCcw className="w-4 h-4" /> Retry</Button>
          <Button className="rounded-md" onClick={onExit} data-testid="typing-back">Back to Passages</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="typing-test">
      {/* Floating glass metric bar */}
      <div className="sticky top-0 z-10 -mx-4 lg:-mx-8 px-4 lg:px-8 py-4 mb-10 bg-card/70 backdrop-blur-xl border-b border-border flex items-center justify-between">
        <button onClick={onExit} className="text-sm text-muted-foreground hover:text-foreground">Exit</button>
        <div className="flex items-center gap-6 font-mono">
          <Metric icon={Zap} label="WPM" value={wpm} testid="live-wpm" />
          <Metric icon={Target} label="ACC" value={`${accuracy}%`} testid="live-accuracy" />
          <Metric icon={Clock} label="TIME" value={`${Math.round(elapsed)}s`} testid="live-time" />
        </div>
        <Button variant="ghost" size="sm" onClick={reset} className="gap-1" data-testid="typing-reset"><RotateCcw className="w-4 h-4" /></Button>
      </div>

      <div
        className="font-mono text-2xl md:text-3xl leading-relaxed tracking-wide mb-8 select-none cursor-text"
        onClick={() => inputRef.current?.focus()}
        data-testid="typing-terminal"
      >
        {target.split("").map((char, i) => {
          let cls = "text-muted-foreground/40";
          if (i < input.length) cls = input[i] === char ? "text-success" : "text-destructive bg-destructive/10 rounded";
          const isCursor = i === input.length;
          return (
            <span key={i} className={`${cls} ${isCursor ? "border-l-2 border-primary blink-caret" : ""}`}>
              {char}
            </span>
          );
        })}
      </div>

      <textarea
        ref={inputRef}
        value={input}
        onChange={handleChange}
        disabled={done}
        data-testid="typing-input"
        className="w-full h-24 p-4 rounded-md border border-border bg-card font-mono text-base focus:outline-none focus:ring-2 focus:ring-primary resize-none"
        placeholder="Start typing here to begin the timer..."
        spellCheck={false}
        autoComplete="off"
      />
      <p className="text-xs text-muted-foreground mt-2 font-mono">{input.length} / {target.length} characters · {errors} errors</p>
    </div>
  );
}

function Metric({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <Icon className="w-4 h-4 text-primary" />
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="font-bold">{value}</span>
    </div>
  );
}

function ResultStat({ icon: Icon, label, value, testid }) {
  return (
    <div className="p-6 rounded-md border border-border bg-card" data-testid={testid}>
      <Icon className="w-5 h-5 text-primary mx-auto mb-2" />
      <p className="font-mono text-3xl font-bold">{value}</p>
      <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mt-1">{label}</p>
    </div>
  );
}
